import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import ReactPaginate from "react-paginate";
import { useStores } from "../../stores";
import { rolesWithId } from "../../constants/constants";
import { apiGetFactFinder, apiPostPut, apiGet } from "../../api/api_methods";
import adminAuthService from "../../services/authService";

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Invalid Date";
  }
};

function FactFinderClients() {
  const { clientsStore } = useStores();

  // Stable user reference
  const user = useMemo(() => {
    const storeUser = clientsStore.user;
    if (storeUser && Object.keys(storeUser).length > 0) {
      return storeUser;
    }
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return storedUser || {};
  }, [clientsStore.user]);

  const isFinancialPlanner = user.role === rolesWithId.agent;

  const [loading, setLoading] = useState(true); // Start with true to show loading on initial mount

  // Pagination state for clients
  const [currentPage, setCurrentPage] = useState(0);
  const [factFinderClients, setFactFinderClients] = useState([]);
  const [factFinderSearch, setFactFinderSearch] = useState("");
  const [factFinderTotalPages, setFactFinderTotalPages] = useState(0);
  // factFinderCount is set but not currently displayed in UI - keeping for future use
  // eslint-disable-next-line no-unused-vars
  const [factFinderCount, setFactFinderCount] = useState(0);
  // Track which client accordion is expanded
  const [expandedClientId, setExpandedClientId] = useState(null);

  // Modal state for creating awaken form
  const [showCreateFormModal, setShowCreateFormModal] = useState(false);
  const [selectedClientData, setSelectedClientData] = useState(null); // Contains row and profile data
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [usedUIds, setUsedUIds] = useState(new Set()); // Track UIDs that already have forms
  const [checkingUIds, setCheckingUIds] = useState(false);
  const [renewalEligibleUIds, setRenewalEligibleUIds] = useState(new Set()); // Track UIDs that have original forms (eligible for renewal)
  const [checkingRenewalEligibility, setCheckingRenewalEligibility] = useState(false);
  
  // Modal state for renewal form
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedRenewalData, setSelectedRenewalData] = useState(null); // Contains client and profile data for renewal
  const [selectedRenewalAgentId, setSelectedRenewalAgentId] = useState("");

  const itemsPerPage = 10;

  // Function to store assignment intent when "Create Awaken Form" is clicked
  const storeAssignmentIntent = useCallback((uId) => {
    if (!uId || !user?._id) return;
    
    const pendingAssignments = JSON.parse(localStorage.getItem('pendingFactFinderAssignments') || '{}');
    pendingAssignments[uId] = {
      agentId: user._id,
      agentName: user.name || user.email,
      timestamp: Date.now()
    };
    localStorage.setItem('pendingFactFinderAssignments', JSON.stringify(pendingAssignments));
    console.log(`Assignment intent stored for UID: ${uId}, Agent: ${user.name || user.email}`);
  }, [user]);

  // Function to assign a client to an agent
  const assignClientToAgent = useCallback(async (formId, agentId, uId) => {
    try {
      const assignBody = {
        formId: formId,
        agentId: agentId,
      };
      
      const assignResponse = await apiPostPut(
        assignBody,
        "/adminPanel/assign-client",
        "PUT"
      );
      
      if (assignResponse.status === 200) {
        console.log(`Client with UID ${uId} automatically assigned to agent ${agentId}`);
        return true;
      } else {
        console.warn(`Failed to auto-assign client with UID ${uId}:`, assignResponse?.body?.message);
        return false;
      }
    } catch (error) {
      console.error(`Error auto-assigning client with UID ${uId}:`, error);
      return false;
    }
  }, []);

  // Note: Assignment by UID endpoint doesn't exist yet
  // The assignment should happen automatically when the form is created
  // because we pass agentId in the form URL. The backend form creation endpoint
  // should handle the agentId parameter and auto-assign the client.

  // Function to fetch agents
  const fetchAgents = useCallback(async () => {
    // If the current user is an agent, avoid calling the admin-only API.
    // Use the logged-in user as the sole selectable agent to prevent 403s.
    if (user?.role === rolesWithId.agent) {
      if (!Array.isArray(clientsStore.agents) || clientsStore.agents.length === 0) {
        clientsStore.setAgents([user]);
      }
      return;
    }

    // Only fetch agents if they're not already loaded
    if (!Array.isArray(clientsStore.agents) || clientsStore.agents.length === 0) {
      try {
        const agentRoleId = "6720e9990ca50db671add43e";
        const apiResponse = await apiGet(`/users/get-users?role=${agentRoleId}`);
        if (apiResponse.status === 200) {
          // Fix: Extract the users array from the response
          const agentsData = apiResponse.body.data?.users || apiResponse.body.users || [];
          clientsStore.setAgents(agentsData);
        } else {
          console.error("Failed to fetch agents:", apiResponse?.message || apiResponse?.body?.message);
        }
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    }
  }, [user, clientsStore]);

  // Helper function to find form by UID (returns form ID and form data)
  const findFormByUId = useCallback(async (uId, includeAllAgents = false) => {
    if (!uId) return null;
    
    const planTypes = ['comprehensive', 'term', 'health', 'pension', 'basic', 'zeroToOne', 'wealthManagement'];
    
    for (const planTypeToCheck of planTypes) {
      try {
        let apiPath;
        // If includeAllAgents is true (for super admin renewal), search without agent filter
        if (includeAllAgents || user.role !== rolesWithId.agent) {
          apiPath = `/adminPanel/get-clients?planType=${planTypeToCheck}&status=&page=1&limit=1000`;
        } else {
          apiPath = `/adminPanel/get-clients?planType=${planTypeToCheck}&status=&agentId=${user._id}&page=1&limit=1000`;
        }
        
        const response = await apiGet(apiPath);
        
        if (response.status === 200 && response.body.success) {
          const clients = response.body.data?.clients || response.body.data || [];
          
          const foundClient = clients.find(client => {
            // Check various UID fields (comprehensive check)
            const clientUId = client.factFinderId ||  // Primary field (backend saves uid here)
                             client.factFinderUId || 
                             client.uId || 
                             client.uid ||
                             client.factFinderUID ||
                             client.factfinderId ||
                             (client.personalDetails && client.personalDetails.factFinderId) ||
                             (client.formData && client.formData.factFinderId) ||
                             (client.awareness?.personalDetails?.factFinderId) ||
                             (client.awareness?.factFinderId);
            
            return clientUId === uId;
          });
          
          if (foundClient && foundClient._id) {
            return { formId: foundClient._id, formData: foundClient };
          }
        }
      } catch (error) {
        console.error(`Error searching ${planTypeToCheck} for UID ${uId}:`, error);
      }
    }
    
    return null;
  }, [user]);

  // Function to check if original forms exist for renewal eligibility
  const checkRenewalEligibility = useCallback(async (uIds) => {
    if (!uIds || uIds.length === 0) return;
    
    setCheckingRenewalEligibility(true);
    const eligibleSet = new Set();
    
    try {
      // Check each UID to see if original form exists
      for (const uId of uIds) {
        const formResult = await findFormByUId(uId, user.role !== rolesWithId.agent);
        if (formResult && formResult.formId) {
          eligibleSet.add(uId);
          console.log(`✅ Original form found for renewal: UID=${uId}, FormID=${formResult.formId}`);
        } else {
          console.log(`❌ Original form NOT found for renewal: UID=${uId}`);
        }
      }
      
      setRenewalEligibleUIds(prev => {
        const newSet = new Set(prev);
        eligibleSet.forEach(uId => newSet.add(uId));
        return newSet;
      });
    } catch (error) {
      console.error("Error checking renewal eligibility:", error);
    } finally {
      setCheckingRenewalEligibility(false);
    }
  }, [findFormByUId, user.role]);

  // Function to fetch existing forms and check for duplicate UIDs
  const checkUsedUIds = useCallback(async (uIds) => {
    if (!uIds || uIds.length === 0) return new Set();
    
    setCheckingUIds(true);
    const usedSet = new Set();
    
    try {
      // Fetch all clients across all plan types to check for existing UIDs
      const planTypes = ['comprehensive', 'term', 'health', 'pension', 'basic', 'zeroToOne', 'wealthManagement'];
      
      for (const planTypeToCheck of planTypes) {
        try {
          let apiPath;
          if (user.role === rolesWithId.agent) {
            apiPath = `/adminPanel/get-clients?planType=${planTypeToCheck}&status=&agentId=${user._id}&page=1&limit=1000`;
          } else {
            apiPath = `/adminPanel/get-clients?planType=${planTypeToCheck}&status=&page=1&limit=1000`;
          }
          
          const response = await apiGet(apiPath);
          
          if (response.status === 200 && response.body.success) {
            const clients = response.body.data?.clients || response.body.data || [];
            
            clients.forEach(client => {
              // Check various UID fields
              const clientUId = client.factFinderId || client.factFinderUId || client.uId || client.uid;
              if (clientUId && uIds.includes(clientUId)) {
                usedSet.add(clientUId);
              }
            });
          }
        } catch (error) {
          console.error(`Error checking ${planTypeToCheck} forms:`, error);
        }
      }
    } catch (error) {
      console.error("Error checking used UIDs:", error);
    } finally {
      setCheckingUIds(false);
    }
    
    return usedSet;
  }, [user]);

  // Fetch Fact Finder clients (profiles)
  const fetchFactFinderClients = useCallback(async () => {
    try {
      setLoading(true);
      const pageNumber = currentPage + 1;
      const searchQuery = factFinderSearch.trim();

      // Build API path - for financial planners, we'll fetch all and filter by email
      // For admins, fetch all clients
      let apiPath;
      if (searchQuery) {
        apiPath = `/Exports/GetClients?page=${pageNumber}&pageSize=${itemsPerPage}&search=${encodeURIComponent(
          searchQuery
        )}`;
      } else {
        apiPath = `/Exports/GetClients?page=${pageNumber}&pageSize=${itemsPerPage}`;
      }

      // If user is a financial planner (agent), try to filter by financial planner email
      // Note: This assumes the Fact Finder API supports filtering by financial planner email
      // If not, we'll filter on the frontend
      const userEmail = user.email?.toLowerCase();

      if (isFinancialPlanner && userEmail) {
        // Add email filter to API call for financial planners
        const separator = apiPath.includes("?") ? "&" : "?";
        apiPath += `${separator}email=${encodeURIComponent(
          userEmail
        )}`;
      }

      console.log("Fetching Fact Finder profiles from:", apiPath);
      const apiResponse = await apiGetFactFinder(apiPath);

      if (apiResponse.status === 200 && apiResponse.body.success) {
        const clients = apiResponse.body.data || [];
        const totalRecords = apiResponse.body.totalRecords || 0;
        const totalPages = apiResponse.body.totalPages || 0;

        // Backend is already filtered by email for agents,
        // so we just use the response as-is to avoid losing records.
        setFactFinderClients(clients);
        setFactFinderCount(totalRecords);
        setFactFinderTotalPages(totalPages);
        setLoading(false);
      } else {
        const errorMessage =
          apiResponse?.body?.message ||
          apiResponse?.body ||
          "Failed to fetch fact finder profiles";
        console.error("Failed to fetch fact finder profiles:", errorMessage);
        setFactFinderClients([]);
        setFactFinderCount(0);
        setFactFinderTotalPages(0);
        setLoading(false);
        // Show user-friendly error message
        if (apiResponse.status === 401) {
          alert("Authentication required. Please log in again.");
        }
      }
    } catch (error) {
      console.error("Error fetching fact finder profiles:", error);
      setFactFinderClients([]);
      setFactFinderCount(0);
      setFactFinderTotalPages(0);
      setLoading(false);
    }
  }, [
    currentPage,
    factFinderSearch,
    itemsPerPage,
    user,
    isFinancialPlanner,
  ]);

  // Function to process pending assignments by checking for clients with matching UIDs
  const processPendingAssignments = useCallback(async () => {
    const pendingAssignments = JSON.parse(localStorage.getItem('pendingFactFinderAssignments') || '{}');
    if (Object.keys(pendingAssignments).length === 0) return;

    const isAdmin = user.role === rolesWithId.admin;
    const processedUIds = [];

    // For financial planners, check their assigned clients list (which they CAN access)
    if (!isAdmin) {
      console.log("Financial planner detected - checking assigned clients for pending assignments");
      console.log("Pending assignments:", Object.keys(pendingAssignments));
      try {
        // Fetch assigned clients for the financial planner
        // Check all plan types to find the client
        const planTypes = ['comprehensive', 'term', 'health', 'pension'];
        let allClients = [];
        
        for (const planType of planTypes) {
          const apiPath = `/adminPanel/get-clients?planType=${planType}&status=&agentId=${user._id}&page=1&limit=100`;
          const response = await apiGet(apiPath);
          
        if (response.status === 200 && response.body.success) {
          const clients = response.body.data?.clients || response.body.data || [];
          allClients = allClients.concat(clients);
          
          // Log first client from each plan type to verify factFinderId is in response
          if (clients.length > 0 && planType === 'comprehensive') {
            console.log(`Sample client from ${planType}:`, {
              _id: clients[0]._id,
              factFinderId: clients[0].factFinderId,
              hasFactFinderId: !!clients[0].factFinderId,
              allKeys: Object.keys(clients[0])
            });
          }
        }
        }
        
        console.log(`Found ${allClients.length} assigned clients across all plan types`);
        
        // Check each client for matching UID (use for...of to handle async properly)
        for (let index = 0; index < allClients.length; index++) {
          const client = allClients[index];
          // Log first few clients with ALL fields for debugging
          if (index < 2) {
            console.log(`Client ${index} - ALL FIELDS:`, client);
            console.log(`Client ${index} - Keys:`, Object.keys(client));
            // Check nested awareness object
            if (client.awareness) {
              console.log(`Client ${index} - awareness keys:`, Object.keys(client.awareness));
              if (client.awareness.personalDetails) {
                console.log(`Client ${index} - awareness.personalDetails keys:`, Object.keys(client.awareness.personalDetails));
              }
            }
            // Deep search for factFinderId in JSON
            const clientJson = JSON.stringify(client);
            const factFinderIdMatch = clientJson.match(/"factFinderId"\s*:\s*"([^"]+)"/);
            if (factFinderIdMatch) {
              console.log(`⚠️ Found factFinderId in JSON string:`, factFinderIdMatch[1]);
            } else {
              console.log(`❌ factFinderId NOT found in client ${index} JSON`);
            }
          }
          
          // Check various UID fields that might be stored (including nested fields)
          // Backend saves uid as factFinderId, so check that first
          const clientUId = client.factFinderId ||  // Primary field (backend saves uid here)
                           client.factFinderUId || 
                           client.uId || 
                           client.uid ||
                           client.factFinderUID ||
                           client.factfinderId ||
                           (client.personalDetails && client.personalDetails.factFinderId) ||
                           (client.formData && client.formData.factFinderId);
          
          if (clientUId && pendingAssignments[clientUId]) {
            const assignment = pendingAssignments[clientUId];
            
            // Check if client is already assigned to the correct agent
            if (client.assignedTo?._id === assignment.agentId) {
              // Already assigned correctly - remove from pending
              console.log(`✅ Found assigned client with UID ${clientUId} - already assigned to ${assignment.agentName}`);
              console.log(`Client details:`, {
                _id: client._id,
                name: client.name,
                assignedTo: client.assignedTo?._id,
                UID: clientUId,
                factFinderId: client.factFinderId
              });
              processedUIds.push(clientUId);
            } else if (!client.assignedTo?._id) {
              // Form created but not assigned yet - assign it now (like createnew.jsx does)
              console.log(`🔄 Form created but not assigned yet. Assigning now...`);
              console.log(`Client details:`, {
                _id: client._id,
                name: client.name,
                UID: clientUId,
                factFinderId: client.factFinderId
              });
              
              // Explicitly assign the client (same as createnew.jsx)
              const assigned = await assignClientToAgent(client._id, assignment.agentId, clientUId);
              if (assigned) {
                console.log(`✅ Successfully assigned client with UID ${clientUId} to ${assignment.agentName}`);
                processedUIds.push(clientUId);
              } else {
                console.warn(`⚠️ Failed to assign client with UID ${clientUId} - will retry later`);
                // Don't remove from pending, will retry next time
              }
            } else {
              // Assigned to different agent - log warning
              console.warn(`⚠️ Client with UID ${clientUId} is assigned to different agent:`, {
                expected: assignment.agentId,
                actual: client.assignedTo?._id,
                clientId: client._id
              });
              // Still remove from pending since it's assigned (just not to us)
              processedUIds.push(clientUId);
            }
            
            // Refresh the Fact Finder clients list to show updated status
            if (processedUIds.includes(clientUId)) {
              setTimeout(() => {
                fetchFactFinderClients();
              }, 1000);
            }
          }
          
          // FALLBACK: Check if this is a newly created client that matches pending assignment
          // This handles cases where backend saves factFinderId but doesn't return it in API response
          if ((client.createdDate || client.createdAt) && Object.keys(pendingAssignments).length > 0) {
            const createdTime = new Date(client.createdDate || client.createdAt).getTime();
            const threeMinutesAgo = Date.now() - (3 * 60 * 1000); // Check last 3 minutes
            
            // If client was created very recently (within 3 minutes) and is assigned to our agent
            // AND we have a pending assignment, it might be the one we're looking for
            if (createdTime > threeMinutesAgo) {
              const clientName = client.name || 
                                (client.awareness && client.awareness.personalDetails && client.awareness.personalDetails.name) ||
                                'Unknown';
              
              // Check if this client is assigned to any of our pending assignment agents
              const pendingUIds = Object.keys(pendingAssignments);
              for (const pendingUId of pendingUIds) {
                const assignment = pendingAssignments[pendingUId];
                
                // If client is assigned to the agent we're expecting AND was created very recently
                // AND doesn't have factFinderId (backend bug), assume it's our match
                if (client.assignedTo?._id === assignment.agentId && !client.factFinderId) {
                  const secondsAgo = Math.round((Date.now() - createdTime) / 1000);
                  const twoMinutesAgo = Date.now() - (2 * 60 * 1000);
                  
                  console.log(`🔍 FALLBACK MATCH: Found recently created client that might match UID ${pendingUId}:`, {
                    _id: client._id,
                    name: clientName,
                    createdSecondsAgo: secondsAgo,
                    assignedTo: client.assignedTo?._id,
                    expectedAgent: assignment.agentId,
                    factFinderId: client.factFinderId,
                    note: 'factFinderId missing - backend projection issue?'
                  });
                  
                  // If created within last 2 minutes and assigned correctly, treat as match
                  if (createdTime > twoMinutesAgo) {
                    console.log(`✅ Treating as match (created ${secondsAgo}s ago, assigned correctly, factFinderId missing)`);
                    console.log(`⚠️ This is a workaround - backend should return factFinderId in API response`);
                    
                    // Mark as processed (already assigned correctly)
                    processedUIds.push(pendingUId);
                    
                    // Refresh the Fact Finder clients list
                    setTimeout(() => {
                      fetchFactFinderClients();
                    }, 1000);
                    
                    break; // Found match, move to next client
                  }
                }
              }
              
              // Log all recently created clients for debugging
              if (index < 3) {
                console.log(`🆕 Recently created client (within 3 min):`, {
                  _id: client._id,
                  name: clientName,
                  createdSecondsAgo: Math.round((Date.now() - createdTime) / 1000),
                  createdAt: client.createdAt,
                  factFinderId: client.factFinderId,
                  assignedTo: client.assignedTo?._id,
                  assignedToName: client.assignedTo?.name,
                  hasFactFinderId: !!client.factFinderId
                });
              }
            }
          }
        }
        
        if (processedUIds.length === 0 && Object.keys(pendingAssignments).length > 0) {
          console.log("⚠️ No matching clients found yet. Pending UIDs:", Object.keys(pendingAssignments));
          console.log("");
          console.log("🔍 DEBUGGING CHECKLIST:");
          console.log("1. Check if form was created:");
          console.log("   - Look for recently created clients (within last 3 minutes)");
          console.log("   - Check if any are assigned to the expected agent");
          console.log("");
          console.log("2. Check backend form creation:");
          console.log("   - Verify /api/awaken/create-form endpoint receives uid parameter");
          console.log("   - Verify form.factFinderId = uid is being saved");
          console.log("   - Query database: db.forms.findOne({ factFinderId: 'MSOW89NSBS' })");
          console.log("");
          console.log("3. Check API response projection:");
          console.log("   - Verify /api/adminPanel/get-clients includes factFinderId in $project");
          console.log("   - Check if factFinderId is excluded by .select() or projection");
          console.log("   - Test API directly: GET /api/adminPanel/get-clients?agentId=...");
          console.log("");
          console.log("🔧 BACKEND FIX REQUIRED:");
          console.log("The backend MUST:");
          console.log("1. Save factFinderId when creating forms: form.factFinderId = req.query.uid");
          console.log("2. Include factFinderId in /adminPanel/get-clients projection");
          console.log("3. Return factFinderId in API response JSON");
          console.log("");
          console.log("Current status: factFinderId is undefined in all API responses");
        }
      } catch (error) {
        console.error("Error checking assigned clients for financial planner:", error);
      }
      
      // Remove processed assignments
      if (processedUIds.length > 0) {
        processedUIds.forEach(uId => {
          delete pendingAssignments[uId];
        });
        localStorage.setItem('pendingFactFinderAssignments', JSON.stringify(pendingAssignments));
        console.log(`Processed ${processedUIds.length} pending assignments for financial planner`);
      }
      
      return;
    }

    // For admins, fetch all clients and check for matches
    try {
      const pageSize = 100;
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const apiPath = `/adminPanel/clients?page=${page}&limit=${pageSize}`;
        const response = await apiGet(apiPath);
        
        if (response.status === 200 && response.body.success) {
          const clients = response.body.data?.clients || response.body.data || [];
          
          // Check each client for matching UID
          clients.forEach(client => {
            if (client.factFinderUId || client.uId) {
              const uId = client.factFinderUId || client.uId;
              if (pendingAssignments[uId] && !client.assignedTo?._id) {
                // Found a match - assign the client
                const assignment = pendingAssignments[uId];
                assignClientToAgent(client._id, assignment.agentId, uId);
                processedUIds.push(uId);
              }
            }
          });

          // Check if there are more pages
          const pagination = response.body.data?.pagination || response.body.pagination || {};
          const totalPages = pagination.totalPages || Math.ceil((pagination.total || 0) / pageSize);
          hasMore = page < totalPages;
          page++;
        } else {
          hasMore = false;
        }
      }
    } catch (error) {
      console.error("Error processing pending assignments:", error);
    }

    // Remove processed assignments
    if (processedUIds.length > 0) {
      processedUIds.forEach(uId => {
        delete pendingAssignments[uId];
      });
      localStorage.setItem('pendingFactFinderAssignments', JSON.stringify(pendingAssignments));
      console.log(`Processed ${processedUIds.length} pending assignments`);
    }
  }, [user, assignClientToAgent, fetchFactFinderClients]);

  // Fetch agents on mount
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  // Check for used UIDs when factFinder clients are loaded
  useEffect(() => {
    if (factFinderClients.length > 0) {
      // Extract all UIDs from factFinder clients
      const allUIds = [];
      const renewalUIds = []; // UIDs that need original form check (for renewal)
      
      factFinderClients.forEach(row => {
        const profiles = row.plans || [];
        profiles.forEach(profile => {
          if (profile.uId && profile.uId.startsWith("MS")) {
            allUIds.push(profile.uId);
            
            // If this is a renewal profile (uIdText contains -R), check if original form exists
            if (profile.uIdText && profile.uIdText.includes("-R")) {
              // The uId field should be the base UID (original form UID)
              const originalUId = profile.uId;
              if (originalUId && !renewalUIds.includes(originalUId)) {
                renewalUIds.push(originalUId);
              }
            }
          }
        });
      });
      
      if (allUIds.length > 0) {
        checkUsedUIds(allUIds).then(usedSet => {
          setUsedUIds(usedSet);
        });
      }
      
      // Check if original forms exist for renewal profiles
      if (renewalUIds.length > 0) {
        checkRenewalEligibility(renewalUIds);
      }
    }
  }, [factFinderClients, checkUsedUIds, checkRenewalEligibility]);

  // Check for used UID when modal opens with specific profile
  useEffect(() => {
    if (showCreateFormModal && selectedClientData?.profile?.uId) {
      checkUsedUIds([selectedClientData.profile.uId]).then(usedSet => {
        setUsedUIds(prev => {
          const newSet = new Set(prev);
          usedSet.forEach(uId => newSet.add(uId));
          return newSet;
        });
      });
    }
  }, [showCreateFormModal, selectedClientData, checkUsedUIds]);

  // Process pending assignments when component mounts or when clients are fetched
  useEffect(() => {
    // Process pending assignments after a short delay to allow for any new clients to be created
    const timer = setTimeout(() => {
      processPendingAssignments();
    }, 2000); // 2 second delay

    return () => clearTimeout(timer);
  }, [factFinderClients, processPendingAssignments]);

  // Initial load + debounced search behaviour
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchFactFinderClients();
    }, 500); // debounce delay in ms

    return () => clearTimeout(handler);
  }, [fetchFactFinderClients, factFinderSearch, currentPage]);

  // Reset to first page when search changes
  useEffect(() => {
    if (factFinderSearch !== undefined) {
      setCurrentPage(0);
      setExpandedClientId(null);
    }
  }, [factFinderSearch]);

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
    // Reset expanded accordion when page changes
    setExpandedClientId(null);
  };

  const renderFactFinderClientsTable = () => {
    const totalPages = factFinderTotalPages;

    return (
      <div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mobile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Advisor Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created On
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profiles
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {factFinderClients.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No fact finder clients found
                  </td>
                </tr>
              ) : (
                factFinderClients.map((client, clientIndex) => {
                  // Use a unique identifier - prefer userId (from API), then id, fallback to index
                  const clientId =
                    client.userId !== undefined && client.userId !== null
                      ? `client-${client.userId}`
                      : client.id !== undefined && client.id !== null
                      ? `client-${client.id}`
                      : `client-index-${clientIndex}`;
                  const isExpanded = expandedClientId === clientId;
                  const profiles = client.plans || [];

                  return (
                    <React.Fragment key={clientId}>
                      {/* Main client row */}
                      <tr className="hover:bg-indigo-50 bg-white transition-colors duration-150 border-b border-gray-100">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setExpandedClientId(
                                  isExpanded ? null : clientId
                                );
                              }}
                              className="text-gray-600 hover:text-blue-600 transition-colors"
                            >
                              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                            <span className="font-medium">
                              {client.name || "N/A"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {client.email || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {client.mobile || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {client.advisorName || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {client.createdDate
                            ? formatDate(client.createdDate)
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                              client.status === "Active"
                                ? "bg-green-100 text-green-700 border border-green-200"
                                : "bg-gray-100 text-gray-700 border border-gray-200"
                            }`}
                          >
                            {client.status || "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {profiles.length}{" "}
                          {profiles.length === 1 ? "Profile" : "Profiles"}
                        </td>
                      </tr>
                      {/* Expanded profiles section */}
                      {isExpanded && profiles.length > 0 && (
                        <tr>
                          <td colSpan={7} className="px-6 py-4 bg-white">
                            <div className="space-y-3">
                              <h4 className="font-semibold text-gray-700 mb-3">
                                Profiles:
                              </h4>
                              <div className="space-y-2">
                                {profiles.map((profile, profileIndex) => (
                                  <div
                                    key={profile.profileId || profileIndex}
                                    className="border border-gray-200 rounded-xl p-5 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                                  >
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                                      <div>
                                        <span className="text-xs text-gray-500">
                                          FF Created Date:
                                        </span>
                                        <p className="text-sm">
                                          {profile.createdDate
                                            ? formatDate(profile.createdDate)
                                            : "N/A"}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-500">
                                          UID:
                                        </span>
                                        <p className="font-mono text-sm font-semibold">
                                          {profile.uId || "N/A"}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-500">
                                          Plan:
                                        </span>
                                        <p className="text-sm">
                                          {profile.awareness?.planDetails
                                            ?.planType ||
                                            profile.plan ||
                                            "N/A"}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-500">
                                          Plan Year:
                                        </span>
                                        <p className="text-sm">
                                          {profile.awareness?.planDetails
                                            ?.planYear ||
                                            profile.planYear ||
                                            "N/A"}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-500">
                                          Client Name:
                                        </span>
                                        <p className="text-sm">
                                          {profile.awareness?.personalDetails
                                            ?.name ||
                                            profile.fullName ||
                                            client.name ||
                                            "N/A"}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="text-xs text-gray-500">
                                          Email:
                                        </span>
                                        <p className="text-sm truncate">
                                          {profile.awareness?.personalDetails
                                            ?.email ||
                                            profile.email ||
                                            client.email ||
                                            "N/A"}
                                        </p>
                                      </div>
                                    </div>
                                    {profile.uId &&
                                      profile.uId.startsWith("MS") && (
                                        <div className="mt-3 pt-3 border-t flex gap-2 items-center">
                                          {/* Check if uIdText contains -R (renewal form) */}
                                          {profile.uIdText && profile.uIdText.includes("-R") ? (
                                            <>
                                              {checkingRenewalEligibility ? (
                                                <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm flex items-center justify-center gap-2">
                                                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-300 border-t-blue-600"></div>
                                                  <span className="text-blue-700 font-medium">Checking form availability...</span>
                                                </div>
                                              ) : renewalEligibleUIds.has(profile.uId) ? (
                                                <button
                                                  onClick={() => {
                                                    // Set selected data for renewal and open modal
                                                    setSelectedRenewalData({ client, profile });
                                                    
                                                    // Pre-select agent based on user role
                                                    if (isFinancialPlanner && user?._id) {
                                                      setSelectedRenewalAgentId(user._id);
                                                    } else {
                                                      setSelectedRenewalAgentId("");
                                                    }
                                                    
                                                    setShowRenewalModal(true);
                                                  }}
                                                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                                                >
                                                  Renew
                                                </button>
                                              ) : (
                                                <div className="flex flex-col gap-2">
                                                  <div className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-xs">
                                                    <p className="text-amber-800 font-medium">
                                                      ⚠️ Original form not found
                                                    </p>
                                                  </div>
                                                  <button
                                                    onClick={async () => {
                                                      // Check if UID is already used
                                                      const existingUIds = await checkUsedUIds([profile.uId]);
                                                      if (existingUIds.has(profile.uId)) {
                                                        alert(`A form with UID ${profile.uId} already exists. Cannot create duplicate form.`);
                                                        return;
                                                      }
                                                      
                                                      // Set selected data and open create form modal
                                                      setSelectedClientData({ client, profile });
                                                      
                                                      // Pre-select agent based on user role
                                                      if (isFinancialPlanner && user?._id) {
                                                        setSelectedAgentId(user._id);
                                                      } else {
                                                        setSelectedAgentId("");
                                                      }
                                                      
                                                      setShowCreateFormModal(true);
                                                    }}
                                                    className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                    disabled={usedUIds.has(profile.uId)}
                                                  >
                                                    {usedUIds.has(profile.uId) ? "Already Created" : "Create Form"}
                                                  </button>
                                                </div>
                                              )}
                                            </>
                                          ) : (
                                            <button
                                              onClick={async () => {
                                                // Check if UID is already used
                                                const existingUIds = await checkUsedUIds([profile.uId]);
                                                if (existingUIds.has(profile.uId)) {
                                                  alert(`A form with UID ${profile.uId} already exists. Cannot create duplicate form.`);
                                                  return;
                                                }
                                                
                                                // Set selected data and open modal
                                                setSelectedClientData({ client, profile });
                                                
                                                // Pre-select agent based on user role
                                                if (isFinancialPlanner && user?._id) {
                                                  setSelectedAgentId(user._id);
                                                } else {
                                                  setSelectedAgentId("");
                                                }
                                                
                                                setShowCreateFormModal(true);
                                              }}
                                              className="px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors text-xs font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                                              disabled={usedUIds.has(profile.uId)}
                                            >
                                              {usedUIds.has(profile.uId) ? "Already Created" : "Create Form"}
                                            </button>
                                          )}
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <ReactPaginate
              nextLabel=">"
              previousLabel="<"
              onPageChange={handlePageChange}
              forcePage={currentPage}
              pageRangeDisplayed={2}
              marginPagesDisplayed={1}
              pageCount={totalPages}
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link px-3 py-2"
              containerClassName="flex items-center gap-1"
              pageClassName="page-item"
              pageLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
              previousClassName="page-item"
              previousLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
              nextClassName="page-item"
              nextLinkClassName="page-link px-3 py-2 rounded hover:bg-gray-100 transition-colors"
              activeClassName="active"
              activeLinkClassName="bg-blue-500 text-white hover:bg-blue-600 font-semibold"
              disabledClassName="opacity-50 cursor-not-allowed"
              disabledLinkClassName="cursor-not-allowed"
              renderOnZeroPageCount={null}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Fact Finder Clients
      </h2>

      {/* Content (always rendered so the input keeps focus) */}
      <div>
        {/* Search input for Fact Finder clients */}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search clients by name, email, or mobile..."
              value={factFinderSearch}
              onChange={(e) => setFactFinderSearch(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setCurrentPage(0);
                  fetchFactFinderClients();
                }
              }}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all shadow-sm"
            />
          </div>
          <button
            onClick={() => {
              setCurrentPage(0);
              fetchFactFinderClients();
            }}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>Search</span>
          </button>
          {factFinderSearch && (
            <button
              onClick={() => {
                setFactFinderSearch("");
                setCurrentPage(0);
                fetchFactFinderClients();
              }}
              className="px-5 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium shadow-sm hover:shadow flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Clear</span>
            </button>
          )}
        </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex flex-col justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
              <span className="text-sm text-gray-600 font-medium">Loading fact finder clients...</span>
            </div>
          ) : factFinderClients.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium text-lg mb-1">No fact finder clients found</p>
              <p className="text-gray-500 text-sm">Try adjusting your search criteria or check back later.</p>
            </div>
          ) : (
            renderFactFinderClientsTable()
          )}
      </div>

      {/* Create Awaken Form Modal */}
      {showCreateFormModal && selectedClientData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Create Awaken Form
            </h3>
            
            {/* Client Information */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Client Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <p className="font-medium">
                    {selectedClientData.profile.awareness?.personalDetails?.name || 
                     selectedClientData.profile.fullName || 
                     selectedClientData.client?.name || 
                     "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">UID:</span>
                  <p className="font-mono font-semibold">{selectedClientData.profile.uId || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-600">Plan Type:</span>
                  <p className="font-medium">
                    {selectedClientData.profile.awareness?.planDetails?.planType || 
                     selectedClientData.profile.plan || 
                     "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <p className="font-medium">
                    {selectedClientData.profile.awareness?.personalDetails?.email || 
                     selectedClientData.profile.email || 
                     selectedClientData.client?.email || 
                     "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Financial Planner Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Financial Planner
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                disabled={isFinancialPlanner}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Financial Planner</option>
                {Array.isArray(clientsStore.agents) &&
                  clientsStore.agents.map((agent) => (
                    <option key={agent._id} value={agent._id}>
                      {agent.name || agent.email}
                    </option>
                  ))}
              </select>
              {isFinancialPlanner && (
                <p className="mt-1 text-xs text-gray-500">
                  You are automatically assigned as the financial planner
                </p>
              )}
            </div>

            {/* Warning if UID already used */}
            {usedUIds.has(selectedClientData.profile.uId) && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  ⚠️ A form with UID <strong>{selectedClientData.profile.uId}</strong> already exists. 
                  Cannot create duplicate form.
                </p>
              </div>
            )}

            {checkingUIds && (
              <div className="mb-4 flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600 text-sm">Checking for existing forms...</span>
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateFormModal(false);
                  setSelectedClientData(null);
                  setSelectedAgentId("");
                }}
                className="px-4 py-2 rounded-lg text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedAgentId) {
                    alert("Please select a financial planner");
                    return;
                  }

                  if (usedUIds.has(selectedClientData.profile.uId)) {
                    alert(`A form with UID ${selectedClientData.profile.uId} already exists. Cannot create duplicate form.`);
                    return;
                  }

                  const profile = selectedClientData.profile;
                  
                  // Store assignment intent before opening form
                  if (user?._id) {
                    storeAssignmentIntent(profile.uId);
                  }
                  
                  // Get the current timestamp when fetching data from Fact Finder
                  // This should be used as createdAt instead of Fact Finder's original date
                  const awakenCreatedAt = new Date().toISOString();
                  
                  // Generate form link with selected agent and fetch timestamp
                  const userRoleForForm = user?.role || null;
                  const formLink = adminAuthService.generateFactFinderFormLink(
                    profile.uId,
                    userRoleForForm,
                    selectedAgentId,
                    awakenCreatedAt
                  );
                  
                  if (formLink) {
                    console.log(`Creating form: UID=${profile.uId}, AgentId=${selectedAgentId}`);
                    console.log(`Form URL: ${formLink}`);
                    
                    // Close modal
                    setShowCreateFormModal(false);
                    setSelectedClientData(null);
                    setSelectedAgentId("");
                    
                    // Mark UID as used
                    setUsedUIds(prev => new Set([...prev, profile.uId]));
                    
                    // If this UID is needed for renewal eligibility, add it
                    // Check if any renewal profiles need this UID
                    const renewalProfilesNeedingThisUId = factFinderClients.flatMap(row => 
                      (row.plans || []).filter(p => 
                        p.uIdText && p.uIdText.includes("-R") && p.uId === profile.uId
                      )
                    );
                    
                    if (renewalProfilesNeedingThisUId.length > 0) {
                      setRenewalEligibleUIds(prev => new Set([...prev, profile.uId]));
                    }
                    
                    // Open form in new tab
                    window.open(formLink, "_blank");
                    
                    // Set up periodic check for newly assigned client (for both agents and superadmin)
                    console.log(`Will check for assigned client with UID ${profile.uId} in 5 seconds...`);
                    setTimeout(() => {
                      processPendingAssignments();
                      fetchFactFinderClients();
                    }, 5000);
                    
                    const handleFocus = () => {
                      setTimeout(() => {
                        console.log("Window focused - checking for newly assigned client...");
                        processPendingAssignments();
                        fetchFactFinderClients();
                      }, 2000);
                      window.removeEventListener('focus', handleFocus);
                    };
                    window.addEventListener('focus', handleFocus);
                  } else {
                    alert("Authentication required. Please log in again.");
                  }
                }}
                disabled={!selectedAgentId || usedUIds.has(selectedClientData.profile.uId) || checkingUIds}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  !selectedAgentId || usedUIds.has(selectedClientData.profile.uId) || checkingUIds
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Create Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renewal Form Modal */}
      {showRenewalModal && selectedRenewalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Create Renewal Form
            </h3>
            
            {/* Client Information */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Client Information</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <p className="font-medium">
                    {selectedRenewalData.profile.awareness?.personalDetails?.name || 
                     selectedRenewalData.profile.fullName || 
                     selectedRenewalData.client?.name || 
                     "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">UID:</span>
                  <p className="font-mono font-semibold">{selectedRenewalData.profile.uId || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-600">Plan Type:</span>
                  <p className="font-medium">
                    {selectedRenewalData.profile.awareness?.planDetails?.planType || 
                     selectedRenewalData.profile.plan || 
                     "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <p className="font-medium">
                    {selectedRenewalData.profile.awareness?.personalDetails?.email || 
                     selectedRenewalData.profile.email || 
                     selectedRenewalData.client?.email || 
                     "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Financial Planner Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Financial Planner
              </label>
              <select
                value={selectedRenewalAgentId}
                onChange={(e) => setSelectedRenewalAgentId(e.target.value)}
                disabled={isFinancialPlanner}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select Financial Planner</option>
                {Array.isArray(clientsStore.agents) &&
                  clientsStore.agents.map((agent) => (
                    <option key={agent._id} value={agent._id}>
                      {agent.name || agent.email}
                    </option>
                  ))}
              </select>
              {isFinancialPlanner && (
                <p className="mt-1 text-xs text-gray-500">
                  You are automatically assigned as the financial planner
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowRenewalModal(false);
                  setSelectedRenewalData(null);
                  setSelectedRenewalAgentId("");
                }}
                className="px-4 py-2 rounded-lg text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedRenewalAgentId) {
                    alert("Please select a financial planner");
                    return;
                  }

                  const profile = selectedRenewalData.profile;
                  const originalUId = profile.uId; // Use the original UID (without -R suffix)
                  
                  try {
                    console.log(`🔍 Searching for original form with UID: ${originalUId}`);
                    
                    // For super admin, search across all agents; for agent, search their forms first
                    const isSuperAdmin = user.role !== rolesWithId.agent;
                    let formResult = null;
                    
                    // Try finding the form
                    if (isSuperAdmin) {
                      // Super admin: search all forms (no agent filter)
                      formResult = await findFormByUId(originalUId, true);
                    } else {
                      // Agent: first try their assigned forms, then try all forms
                      formResult = await findFormByUId(originalUId, false);
                      if (!formResult) {
                        console.log(`⚠️ Form not found in agent's assigned forms, trying all forms...`);
                        formResult = await findFormByUId(originalUId, true);
                      }
                    }
                    
                    if (!formResult || !formResult.formId) {
                      console.error(`❌ Could not find original form with UID: ${originalUId}`);
                      console.error(`User role: ${user.role === rolesWithId.agent ? 'Agent' : 'Super Admin'}`);
                      
                      alert(`Could not find the original form with UID ${originalUId}.\n\n` +
                            `This might happen if:\n` +
                            `- The form doesn't exist yet in Awaken\n` +
                            `- The form is assigned to a different financial planner (if you're an agent)\n` +
                            `- The UID is stored differently in the database\n\n` +
                            `Please ensure the form exists and try again.`);
                      return;
                    }
                    
                    const originalFormId = formResult.formId;
                    console.log(`✅ Found original form: ID=${originalFormId}, UID=${originalUId}`);
                    
                    // Generate renewal form link using the original form ID
                    const currentYear = new Date().getFullYear();
                    const renewalLink = adminAuthService.generateRenewalFormLink(
                      originalFormId,
                      currentYear,
                      user.role,
                      selectedRenewalAgentId
                    );
                    
                    if (renewalLink) {
                      // Mark the original form as having a renewal created
                      // Backend will automatically filter this from locked awakens
                      try {
                        const markRenewalResponse = await apiPostPut(
                          {
                            renewalCreated: true,
                            renewalCreatedDate: new Date().toISOString(),
                            renewalCreatedBy: user._id,
                          },
                          `/adminPanel/change-form-status/${originalFormId}`,
                          "PUT"
                        );
                        
                        if (markRenewalResponse.status === 200) {
                          console.log("✅ Original form marked as having renewal created");
                        } else {
                          console.warn("⚠️ Failed to mark form as renewed, but continuing with renewal creation");
                        }
                      } catch (markError) {
                        console.error("Error marking form as renewed:", markError);
                        // Continue with renewal creation even if marking fails
                      }
                      
                      console.log(`Creating renewal form: Original Form ID=${originalFormId}, UID=${originalUId}, AgentId=${selectedRenewalAgentId}`);
                      console.log(`Renewal Form URL: ${renewalLink}`);
                      
                      // Close modal
                      setShowRenewalModal(false);
                      setSelectedRenewalData(null);
                      setSelectedRenewalAgentId("");
                      
                      // Open form in new tab
                      window.open(renewalLink, "_blank");
                    } else {
                      alert("Authentication required. Please log in again.");
                    }
                  } catch (error) {
                    console.error("Error creating renewal form:", error);
                    alert("Error creating renewal form. Please try again.");
                  }
                }}
                disabled={!selectedRenewalAgentId}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  !selectedRenewalAgentId
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-purple-600 text-white hover:bg-purple-700"
                }`}
              >
                Create Renewal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FactFinderClients;


